import { useEffect, useState } from 'react';
import {
  useMarkAsRead,
  useUnmarkAsRead,
  useReadIds,
} from 'hooks/api/backend/user/readState/readState';
import { useFirebase } from 'hooks/useFirebase';

type UseIsRead = {
  isRead: boolean | null;
  handleReadChange: (title: string) => Promise<void>;
};

export const useIsRead = (id: string | undefined): UseIsRead => {
  const [isRead, setIsRead] = useState<boolean | null>(null);
  const markAsReadMutation = useMarkAsRead();
  const unmarkAsReadMutation = useUnmarkAsRead();
  const { data: allReadIds } = useReadIds();
  const { logFirebaseClick } = useFirebase();

  useEffect(() => {
    if (allReadIds && id) {
      setIsRead(allReadIds.readIds.includes(id));
    }
  }, [allReadIds, setIsRead, id]);

  const handleReadChange = async (title: string): Promise<void> => {
    if (!id) return;
    // useMarkAsRead/useUnmarkAsRead patch the shared `['read-state']` cache
    // directly on success, so every other mounted read indicator picks up
    // the change without a refetch. On failure their onError shows a toast;
    // mutateAsync still rejects after that, so we catch it here — otherwise
    // it's an unhandled rejection, and `setIsRead` below correctly never
    // fires, leaving the button reflecting the failed state.
    try {
      if (isRead) {
        logFirebaseClick('unmark-as-read', title);
        await unmarkAsReadMutation.mutateAsync(id);
        setIsRead(false);
      } else {
        logFirebaseClick('mark-as-read', title);
        await markAsReadMutation.mutateAsync(id);
        setIsRead(true);
      }
    } catch {
      // Already surfaced via toast in useMarkAsRead/useUnmarkAsRead's onError.
    }
  };

  return { isRead, handleReadChange };
};
